import { useCallback, useMemo } from 'react';

import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Tag } from 'primereact/tag';
import { useTranslation } from 'react-i18next';

import {
  FertilizationWithEmbryos,
  TOA_GetFertilizationsForFreezing,
} from '@/rtk-query.gql';

import { SelectionService } from '../utils/selectionService';
import { SelectionSummary } from '../utils/types';

type SelectionSummaryDialogProps = {
  visible: boolean;
  onHide: () => void;
  onAccept: () => void;
  onReject: () => void;
  fertEmbryosData: FertilizationWithEmbryos[];
  fertilizations: TOA_GetFertilizationsForFreezing[];
};

export const SelectionSummaryDialog = ({
  visible,
  onHide,
  onAccept,
  onReject,
  fertEmbryosData,
  fertilizations,
}: SelectionSummaryDialogProps) => {
  const { t } = useTranslation();

  const buildSelectionSummary = useCallback((): SelectionSummary | null => {
    const selection = SelectionService.getSelection();
    const { tempEmbryoIds, liveEmbryoIds, fertilizationIds } = selection;

    if (
      !tempEmbryoIds.length &&
      !liveEmbryoIds.length &&
      !fertilizationIds.length
    ) {
      return null;
    }

    const plateMap = new Map<
      number,
      { tempCount: number; liveCount: number }
    >();

    for (const fert of fertEmbryosData) {
      const fertInfo = fertilizations.find((f) => f.fertId === fert.fertId);
      const plateNumber = fertInfo?.plateNumber ?? 0;

      const tempCount = (fert.tempEmbryos || []).filter((emb) =>
        tempEmbryoIds.includes(emb.embryoFreezeEntryId as string),
      ).length;

      const liveCount = (fert.liveEmbryos || []).filter((emb) =>
        liveEmbryoIds.includes(emb.id as string),
      ).length;

      if (tempCount > 0 || liveCount > 0) {
        const existing = plateMap.get(plateNumber) || {
          tempCount: 0,
          liveCount: 0,
        };
        plateMap.set(plateNumber, {
          tempCount: existing.tempCount + tempCount,
          liveCount: existing.liveCount + liveCount,
        });
      }
    }

    const items = Array.from(plateMap.entries())
      .map(([plateNumber, counts]) => ({
        plateNumber,
        tempEmbryoCount: counts.tempCount,
        liveEmbryoCount: counts.liveCount,
      }))
      .sort((a, b) => a.plateNumber - b.plateNumber);

    return {
      items,
      totalTempEmbryos: tempEmbryoIds.length,
      totalLiveEmbryos: liveEmbryoIds.length,
      totalFertilizations: fertilizationIds.length,
    };
  }, [fertEmbryosData, fertilizations]);

  const selectionSummary = useMemo(() => {
    if (!visible) return null;
    return buildSelectionSummary();
  }, [visible, buildSelectionSummary]);

  const footer = (
    <div className="flex justify-end gap-2">
      <Button
        severity="secondary"
        label={t('common.cancel')}
        icon="pi pi-times"
        onClick={onReject}
      />
      <Button
        severity="success"
        label={t('common.proceed')}
        icon="pi pi-check"
        onClick={onAccept}
        autoFocus
      />
    </div>
  );

  return (
    <Dialog
      header={t('ovaDrive.confirmUpdate')}
      visible={visible}
      onHide={onHide}
      className="w-[40vw]"
      footer={footer}
    >
      <div className="flex flex-col gap-4">
        <p className="text-lg font-medium">
          {t('ovaDrive.confirmUpdateMessage')}
        </p>

        {selectionSummary && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <div className="mb-3 flex gap-6 text-sm font-semibold">
              <span>
                {t('common.total')}: {selectionSummary.totalTempEmbryos}{' '}
                {t('tempEmbryo.tempEmbryos')},{' '}
                {selectionSummary.totalLiveEmbryos} {t('embryo.liveEmbryos')}
              </span>
            </div>

            <DataTable
              value={selectionSummary.items}
              scrollable
              scrollHeight="300px"
              className="text-sm"
            >
              <Column
                field="plateNumber"
                header={t('fertilization.plate')}
                body={(rowData) =>
                  `${t('fertilization.plate')} ${rowData.plateNumber}`
                }
                style={{ width: '33%' }}
              />
              <Column
                field="tempEmbryoCount"
                header={t('tempEmbryo.tempEmbryos')}
                body={(rowData) =>
                  rowData.tempEmbryoCount > 0 ? (
                    <Tag value={rowData.tempEmbryoCount} severity="info" />
                  ) : (
                    <span className="text-gray-400">-</span>
                  )
                }
                align="center"
                style={{ width: '33%' }}
              />
              <Column
                field="liveEmbryoCount"
                header={t('embryo.liveEmbryos')}
                body={(rowData) =>
                  rowData.liveEmbryoCount > 0 ? (
                    <Tag value={rowData.liveEmbryoCount} severity="success" />
                  ) : (
                    <span className="text-gray-400">-</span>
                  )
                }
                align="center"
                style={{ width: '34%' }}
              />
            </DataTable>
          </div>
        )}
      </div>
    </Dialog>
  );
};
